import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Divider,
  Stack,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Collapse,
  Badge,
  InputAdornment
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Edit as OverrideIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Verified as VerifiedIcon,
  SmartToy as AutoAssignIcon,
  ShowChart as ChartIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import api from '../../services/api';

const TriageReviewView = ({ token }) => {
  // View mode state
  const [viewMode, setViewMode] = useState('pending'); // 'pending', 'approved', or 'statistics'

  // Pending reviews state
  const [pendingReviews, setPendingReviews] = useState([]);

  // Approved triages state
  const [approvedTriages, setApprovedTriages] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [approvedPage, setApprovedPage] = useState(1);
  const [approvedTotalPages, setApprovedTotalPages] = useState(1);
  const [approvedTotalCount, setApprovedTotalCount] = useState(0);

  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('all'); // Default to All Time to show all data
  const [statsDateFrom, setStatsDateFrom] = useState('');
  const [statsDateTo, setStatsDateTo] = useState('');
  const [statsFlowFilter, setStatsFlowFilter] = useState('');

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    approvalType: 'all',
    onboardingFlowId: '',
    approverUserId: '',
    severity: 'all',
    diagnosis: []
  });

  // Available options for filters
  const [availableFlows, setAvailableFlows] = useState([]);
  const [availableApprovers, setAvailableApprovers] = useState([]);

  // Common state
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [overridePersonaKey, setOverridePersonaKey] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // Sorting state for pending reviews
  const [pendingSortBy, setPendingSortBy] = useState('evaluatedAt');
  const [pendingSortOrder, setPendingSortOrder] = useState('desc');

  // Sorting state for approved triages
  const [approvedSortBy, setApprovedSortBy] = useState('evaluatedAt');
  const [approvedSortOrder, setApprovedSortOrder] = useState('desc');

  // Search/filter state for pending reviews
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (viewMode === 'approved') {
      fetchApprovedTriages();
    } else if (viewMode === 'statistics') {
      fetchStatistics();
    }
  }, [viewMode, approvedPage, filters]);

  useEffect(() => {
    if (viewMode === 'statistics') {
      fetchStatistics();
    }
  }, [statsPeriod, statsDateFrom, statsDateTo, statsFlowFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [queueRes, personasRes] = await Promise.all([
        api.get('/triage/pending'),
        api.get('/triage/available-personas')
      ]);

      setPendingReviews(queueRes.data.pendingReviews || []);
      setAvailablePersonas(personasRes.data.personas || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching triage data:', err);
      setError(err.response?.data?.msg || 'Failed to load triage queue');
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Fetch onboarding flows (use triage endpoint which is accessible to clinical supervisors)
      const flowsRes = await api.get('/triage/available-flows');
      setAvailableFlows(flowsRes.data.flows || []);

      // Fetch users who can approve (admins and clinical supervisors)
      const usersRes = await api.get('/admin/users');
      const approvers = (usersRes.data.users || []).filter(user =>
        user.role && (user.role.includes('admin') || user.role.includes('clinical_supervisor'))
      );
      setAvailableApprovers(approvers);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      // Non-critical error, don't show to user
    }
  };

  const fetchApprovedTriages = async () => {
    try {
      setApprovedLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append('page', approvedPage);
      params.append('limit', '20');

      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.approvalType !== 'all') params.append('approvalType', filters.approvalType);
      if (filters.onboardingFlowId) params.append('onboardingFlowId', filters.onboardingFlowId);
      if (filters.approverUserId) params.append('approverUserId', filters.approverUserId);
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.diagnosis.length > 0) params.append('diagnosis', filters.diagnosis.join(','));

      const res = await api.get(`/triage/approved?${params.toString()}`);

      setApprovedTriages(res.data.approvedTriages || []);
      setApprovedTotalCount(res.data.totalCount || 0);
      setApprovedTotalPages(res.data.totalPages || 1);
      setApprovedLoading(false);
    } catch (err) {
      console.error('Error fetching approved triages:', err);
      setError(err.response?.data?.msg || 'Failed to load approved triages');
      setApprovedLoading(false);
    }
  };

  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
    if (newValue === 'pending') {
      fetchData(); // Refresh pending queue
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setApprovedPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      approvalType: 'all',
      onboardingFlowId: '',
      approverUserId: '',
      severity: 'all',
      diagnosis: []
    });
    setApprovedPage(1);
  };

  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append('period', statsPeriod);

      if (statsPeriod === 'custom') {
        if (statsDateFrom) params.append('dateFrom', statsDateFrom);
        if (statsDateTo) params.append('dateTo', statsDateTo);
      }

      if (statsFlowFilter) params.append('onboardingFlowId', statsFlowFilter);

      const res = await api.get(`/triage/statistics?${params.toString()}`);

      console.log('Statistics API response:', res.data);
      console.log('approvalTrend:', res.data?.approvalTrend);
      console.log('severityDistribution:', res.data?.severityDistribution);
      console.log('diagnosisDistribution:', res.data?.diagnosisDistribution);
      console.log('flows:', res.data?.flows);

      setStatistics(res.data);
      setStatsLoading(false);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.response?.data?.msg || 'Failed to load statistics');
      setStatsLoading(false);
    }
  };

  const handleStatsPeriodChange = (newPeriod) => {
    setStatsPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setStatsDateFrom('');
      setStatsDateTo('');
    }
  };

  const handleViewDetails = async (review) => {
    try {
      const res = await api.get(`/triage/${review.conversationId}/details`);
      setSelectedReview(res.data.triage);
      setDetailsDialogOpen(true);
    } catch (err) {
      console.error('Error fetching triage details:', err);
      setError(err.response?.data?.msg || 'Failed to load triage details');
    }
  };

  const handleApprove = async (review) => {
    if (!window.confirm(`Approve AI recommendation to assign "${review.recommendation.recommendedPersonaKey}" to ${review.client.name}?`)) {
      return;
    }

    try {
      await api.post(`/triage/${review.conversationId}/approve`);
      setSuccess(`Successfully assigned persona to ${review.client.name}`);
      fetchData(); // Refresh the queue
    } catch (err) {
      console.error('Error approving triage:', err);
      setError(err.response?.data?.msg || 'Failed to approve triage');
    }
  };

  const handleOpenOverride = (review) => {
    setSelectedReview(review);
    setOverridePersonaKey(review.recommendation.recommendedPersonaKey || '');
    setOverrideReason('');
    setOverrideDialogOpen(true);
  };

  const handleOverrideSubmit = async () => {
    if (!overridePersonaKey) {
      setError('Please select a persona');
      return;
    }

    if (!overrideReason || overrideReason.trim().length < 10) {
      setError('Override reason must be at least 10 characters');
      return;
    }

    try {
      await api.post(`/triage/${selectedReview.conversationId}/override`, {
        personaKey: overridePersonaKey,
        overrideReason: overrideReason
      });

      setSuccess(`Successfully overridden and assigned persona to ${selectedReview.client.name}`);
      setOverrideDialogOpen(false);
      setSelectedReview(null);
      setOverridePersonaKey('');
      setOverrideReason('');
      fetchData(); // Refresh the queue
    } catch (err) {
      console.error('Error overriding triage:', err);
      setError(err.response?.data?.msg || 'Failed to override triage');
    }
  };

  const getSeverityColor = (score, domain) => {
    // Color coding based on severity
    const domainThresholds = {
      depression: { mild: 6, moderate: 10, severe: 15 },
      anxiety: { mild: 6, moderate: 10, severe: 15 },
      psychosis: { mild: 3, moderate: 6, severe: 10 },
      'self-harm': { mild: 1, moderate: 1, severe: 1 }, // Always critical
      trauma: { mild: 4, moderate: 8, severe: 12 }
    };

    const threshold = domainThresholds[domain] || { mild: 5, moderate: 10, severe: 15 };

    if (score >= threshold.severe) return 'error';
    if (score >= threshold.moderate) return 'warning';
    if (score >= threshold.mild) return 'info';
    return 'success';
  };

  const getSeverityLevelColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getScoreSeverityColor = (score, maxScore) => {
    if (!maxScore) return 'default';

    const percentage = (score / maxScore) * 100;

    if (percentage >= 75) return 'error'; // High severity (red)
    if (percentage >= 50) return 'warning'; // Moderate severity (orange)
    if (percentage >= 25) return 'info'; // Mild severity (blue)
    return 'success'; // Minimal/None (green)
  };

  const formatApprovalType = (type) => {
    switch (type) {
      case 'auto_assigned':
        return { label: 'Auto-Assigned', icon: <AutoAssignIcon fontSize="small" />, color: 'primary' };
      case 'approved':
        return { label: 'Manually Approved', icon: <VerifiedIcon fontSize="small" />, color: 'success' };
      case 'overridden':
        return { label: 'Overridden', icon: <OverrideIcon fontSize="small" />, color: 'warning' };
      default:
        return { label: 'Unknown', icon: null, color: 'default' };
    }
  };

  // Sorting handlers
  const handlePendingSort = (column) => {
    const isAsc = pendingSortBy === column && pendingSortOrder === 'asc';
    setPendingSortOrder(isAsc ? 'desc' : 'asc');
    setPendingSortBy(column);
  };

  const handleApprovedSort = (column) => {
    const isAsc = approvedSortBy === column && approvedSortOrder === 'asc';
    setApprovedSortOrder(isAsc ? 'desc' : 'asc');
    setApprovedSortBy(column);
  };

  // Filter and sort pending reviews
  const filteredPendingReviews = pendingReviews.filter((review) => {
    if (!pendingSearchTerm) return true;
    const searchLower = pendingSearchTerm.toLowerCase();
    return (
      review.client?.name?.toLowerCase().includes(searchLower) ||
      review.client?.email?.toLowerCase().includes(searchLower) ||
      review.flow?.name?.toLowerCase().includes(searchLower) ||
      review.recommendation?.recommendedPersonaKey?.toLowerCase().includes(searchLower)
    );
  });

  const sortedPendingReviews = [...filteredPendingReviews].sort((a, b) => {
    let aVal, bVal;

    switch (pendingSortBy) {
      case 'name':
        aVal = a.client?.name?.toLowerCase() || '';
        bVal = b.client?.name?.toLowerCase() || '';
        break;
      case 'flow':
        aVal = a.flow?.name?.toLowerCase() || '';
        bVal = b.flow?.name?.toLowerCase() || '';
        break;
      case 'evaluatedAt':
        aVal = new Date(a.evaluatedAt || 0).getTime();
        bVal = new Date(b.evaluatedAt || 0).getTime();
        break;
      case 'persona':
        aVal = a.recommendation?.recommendedPersonaKey?.toLowerCase() || '';
        bVal = b.recommendation?.recommendedPersonaKey?.toLowerCase() || '';
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return pendingSortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return pendingSortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Sort approved triages
  const sortedApprovedTriages = [...approvedTriages].sort((a, b) => {
    let aVal, bVal;

    switch (approvedSortBy) {
      case 'name':
        aVal = a.client?.name?.toLowerCase() || '';
        bVal = b.client?.name?.toLowerCase() || '';
        break;
      case 'flow':
        aVal = a.flow?.name?.toLowerCase() || '';
        bVal = b.flow?.name?.toLowerCase() || '';
        break;
      case 'evaluatedAt':
        aVal = new Date(a.evaluatedAt || 0).getTime();
        bVal = new Date(b.evaluatedAt || 0).getTime();
        break;
      case 'persona':
        aVal = a.assignedPersona?.toLowerCase() || '';
        bVal = b.assignedPersona?.toLowerCase() || '';
        break;
      case 'severity':
        const severityOrder = { critical: 4, high: 3, medium: 2, low: 1, minimal: 0 };
        aVal = severityOrder[a.severity] || 0;
        bVal = severityOrder[b.severity] || 0;
        break;
      case 'approvalType':
        aVal = a.approval?.type?.toLowerCase() || '';
        bVal = b.approval?.type?.toLowerCase() || '';
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return approvedSortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return approvedSortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading && viewMode === 'pending') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom>
          <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Triage Reviews
        </Typography>
        <Chip
          icon={<GroupIcon />}
          label="Viewing: Your Service Groups"
          color="primary"
          variant="outlined"
          size="small"
        />
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Review pending triage decisions and view approved triage history
      </Typography>

      {/* Tabs for switching between Pending and Approved views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={viewMode} onChange={handleViewModeChange}>
          <Tab
            label={
              <Badge badgeContent={pendingReviews.length} color="error" max={99}>
                <span style={{ paddingRight: pendingReviews.length > 0 ? '20px' : '0' }}>
                  Pending Reviews
                </span>
              </Badge>
            }
            value="pending"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                Approved Triages
                {approvedTotalCount > 0 && (
                  <Chip label={approvedTotalCount} size="small" color="primary" />
                )}
              </Box>
            }
            value="approved"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChartIcon fontSize="small" />
                Statistics
              </Box>
            }
            value="statistics"
          />
        </Tabs>
      </Box>

      {/* Pending Reviews View */}
      {viewMode === 'pending' && (
        <>
          <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Alert severity="info" sx={{ flexGrow: 1 }}>
              {pendingSearchTerm ? (
                <>
                  <strong>{filteredPendingReviews.length}</strong> of <strong>{pendingReviews.length}</strong> conversation{pendingReviews.length !== 1 ? 's' : ''} matching search
                </>
              ) : (
                <>
                  <strong>{pendingReviews.length}</strong> conversation{pendingReviews.length !== 1 ? 's' : ''} awaiting triage review
                </>
              )}
            </Alert>
            <TextField
              size="small"
              placeholder="Search by name, email, flow, or persona..."
              value={pendingSearchTerm}
              onChange={(e) => setPendingSearchTerm(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: pendingSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setPendingSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {sortedPendingReviews.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                {pendingSearchTerm ? 'No matching triage reviews' : 'No pending triage reviews'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {pendingSearchTerm
                  ? `No results found for "${pendingSearchTerm}". Try a different search term.`
                  : 'All users have been assigned to therapist personas'}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={pendingSortBy === 'name'}
                        direction={pendingSortBy === 'name' ? pendingSortOrder : 'asc'}
                        onClick={() => handlePendingSort('name')}
                      >
                        Client
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={pendingSortBy === 'flow'}
                        direction={pendingSortBy === 'flow' ? pendingSortOrder : 'asc'}
                        onClick={() => handlePendingSort('flow')}
                      >
                        Flow
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={pendingSortBy === 'evaluatedAt'}
                        direction={pendingSortBy === 'evaluatedAt' ? pendingSortOrder : 'asc'}
                        onClick={() => handlePendingSort('evaluatedAt')}
                      >
                        Assessment Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Primary Concern</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={pendingSortBy === 'persona'}
                        direction={pendingSortBy === 'persona' ? pendingSortOrder : 'asc'}
                        onClick={() => handlePendingSort('persona')}
                      >
                        Recommended Persona
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Matched Rule</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPendingReviews.map((review) => (
                    <TableRow key={review.conversationId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {review.client.name || 'Anonymous'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {review.client.email}
                        </Typography>
                        {review.client.age && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Age: {review.client.age}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {review.flow.name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {new Date(review.evaluatedAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.evaluatedAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {review.assessment?.flaggedDomains?.map((domain) => {
                            const score = review.assessment.domainScores?.[domain];
                            return (
                              <Chip
                                key={domain}
                                label={`${domain}: ${score || 'N/A'}`}
                                size="small"
                                color={getSeverityColor(score, domain)}
                                sx={{ m: 0.25 }}
                              />
                            );
                          })}
                          {review.assessment.flaggedDomains?.length === 0 && (
                            <Chip label="No flags" size="small" color="success" />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={<PersonIcon />}
                          label={review.recommendation.recommendedPersonaKey}
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        {review.recommendation.matchedRule ? (
                          <>
                            <Typography variant="body2" fontWeight="bold">
                              {review.recommendation.matchedRule.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Priority: {review.recommendation.matchedRule.priority}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No rule matched
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewDetails(review)}
                            title="View Full Details"
                          >
                            <ViewIcon />
                          </IconButton>

                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(review)}
                            title="Approve Recommendation"
                          >
                            <ApproveIcon />
                          </IconButton>

                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleOpenOverride(review)}
                            title="Override with Different Persona"
                          >
                            <OverrideIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Approved Triages View */}
      {viewMode === 'approved' && (
        <>
          {/* Filter Panel */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mb: 2 }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>

            <Collapse in={showFilters}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Filter Approved Triages
                </Typography>

                <Grid container spacing={2}>
                  {/* Date Range */}
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Date From"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Date To"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Approval Type */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Approval Type</InputLabel>
                      <Select
                        value={filters.approvalType}
                        label="Approval Type"
                        onChange={(e) => handleFilterChange('approvalType', e.target.value)}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="auto_assigned">Auto-Assigned</MenuItem>
                        <MenuItem value="approved">Manually Approved</MenuItem>
                        <MenuItem value="overridden">Overridden</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Severity */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Severity</InputLabel>
                      <Select
                        value={filters.severity}
                        label="Severity"
                        onChange={(e) => handleFilterChange('severity', e.target.value)}
                      >
                        <MenuItem value="all">All Levels</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="minimal">Minimal</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Onboarding Flow */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Onboarding Flow</InputLabel>
                      <Select
                        value={filters.onboardingFlowId}
                        label="Onboarding Flow"
                        onChange={(e) => handleFilterChange('onboardingFlowId', e.target.value)}
                      >
                        <MenuItem value="">All Flows</MenuItem>
                        {availableFlows.map((flow) => (
                          <MenuItem key={flow._id} value={flow._id}>
                            {flow.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Approver */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Approved By</InputLabel>
                      <Select
                        value={filters.approverUserId}
                        label="Approved By"
                        onChange={(e) => handleFilterChange('approverUserId', e.target.value)}
                      >
                        <MenuItem value="">All Approvers</MenuItem>
                        {availableApprovers.map((approver) => (
                          <MenuItem key={approver._id} value={approver._id}>
                            {`${approver.firstName || ''} ${approver.lastName || ''}`.trim() || approver.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Diagnosis */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Primary Diagnosis</InputLabel>
                      <Select
                        multiple
                        value={filters.diagnosis}
                        label="Primary Diagnosis"
                        onChange={(e) => handleFilterChange('diagnosis', e.target.value)}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        <MenuItem value="depression">Depression</MenuItem>
                        <MenuItem value="anxiety">Anxiety</MenuItem>
                        <MenuItem value="trauma">Trauma/PTSD</MenuItem>
                        <MenuItem value="psychosis">Psychosis</MenuItem>
                        <MenuItem value="self-harm">Self-Harm</MenuItem>
                        <MenuItem value="multi-domain">Multi-Domain</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Clear Filters Button */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      sx={{ height: '56px' }}
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Collapse>
          </Box>

          {/* Approved Triages Table */}
          {approvedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : approvedTriages.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No approved triages found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Object.values(filters).some(v => v && (Array.isArray(v) ? v.length > 0 : v !== 'all'))
                  ? 'Try adjusting your filters'
                  : 'Approved triages will appear here'}
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={approvedSortBy === 'name'}
                          direction={approvedSortBy === 'name' ? approvedSortOrder : 'asc'}
                          onClick={() => handleApprovedSort('name')}
                        >
                          Client
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={approvedSortBy === 'flow'}
                          direction={approvedSortBy === 'flow' ? approvedSortOrder : 'asc'}
                          onClick={() => handleApprovedSort('flow')}
                        >
                          Flow
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={approvedSortBy === 'severity'}
                          direction={approvedSortBy === 'severity' ? approvedSortOrder : 'asc'}
                          onClick={() => handleApprovedSort('severity')}
                        >
                          Diagnosis/Severity
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={approvedSortBy === 'persona'}
                          direction={approvedSortBy === 'persona' ? approvedSortOrder : 'asc'}
                          onClick={() => handleApprovedSort('persona')}
                        >
                          Assigned Persona
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={approvedSortBy === 'approvalType'}
                          direction={approvedSortBy === 'approvalType' ? approvedSortOrder : 'asc'}
                          onClick={() => handleApprovedSort('approvalType')}
                        >
                          Approval Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Approved By</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={approvedSortBy === 'evaluatedAt'}
                          direction={approvedSortBy === 'evaluatedAt' ? approvedSortOrder : 'asc'}
                          onClick={() => handleApprovedSort('evaluatedAt')}
                        >
                          Approval Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedApprovedTriages.map((triage) => {
                      const approvalInfo = formatApprovalType(triage.approval.type);

                      return (
                        <TableRow key={triage.conversationId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {triage.client.name || 'Anonymous'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {triage.client.email}
                            </Typography>
                            {triage.client.age && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Age: {triage.client.age}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {triage.flow.name}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5}>
                              <Chip
                                label={`Severity: ${triage.severity.toUpperCase()}`}
                                size="small"
                                color={getSeverityLevelColor(triage.severity)}
                              />
                              {triage.primaryDiagnosis && triage.primaryDiagnosis !== 'none' && (
                                <Chip
                                  label={triage.primaryDiagnosis}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {Object.entries(triage.assessment?.domainScores || {}).slice(0, 3).map(([domain, score]) => (
                                <Chip
                                  key={domain}
                                  label={`${domain}: ${score}`}
                                  size="small"
                                  color={getSeverityColor(score, domain)}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Chip
                              icon={<PersonIcon />}
                              label={triage.assignedPersona}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              icon={approvalInfo.icon}
                              label={approvalInfo.label}
                              color={approvalInfo.color}
                              size="small"
                            />
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {triage.approval.approvedBy
                                ? triage.approval.approvedBy.name || triage.approval.approvedBy.email
                                : 'System'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {new Date(triage.approval.approvedAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(triage.approval.approvedAt).toLocaleTimeString()}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(triage)}
                              title="View Full Details"
                            >
                              <ViewIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={approvedTotalPages}
                  page={approvedPage}
                  onChange={(event, value) => setApprovedPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* Statistics View */}
      {viewMode === 'statistics' && (
        <>
          {/* Time Period Selector */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={statsPeriod}
                label="Time Period"
                onChange={(e) => handleStatsPeriodChange(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {statsPeriod === 'custom' && (
              <>
                <TextField
                  label="From Date"
                  type="date"
                  value={statsDateFrom}
                  onChange={(e) => setStatsDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  value={statsDateTo}
                  onChange={(e) => setStatsDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Flow Filter</InputLabel>
              <Select
                value={statsFlowFilter}
                label="Flow Filter"
                onChange={(e) => setStatsFlowFilter(e.target.value)}
              >
                <MenuItem value="">All Flows</MenuItem>
                {availableFlows.map(flow => (
                  <MenuItem key={flow._id} value={flow._id}>{flow.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton
              onClick={fetchStatistics}
              title="Refresh Statistics"
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Box>

          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : !statistics ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No statistics available
              </Typography>
            </Paper>
          ) : statistics.summary?.totalTriages === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No triage data found for {statistics.period?.label || 'selected period'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {statsPeriod !== 'all' ? (
                  <>Try selecting a different time period or choose "All Time" to see all historical data.</>
                ) : (
                  <>No triages have been processed yet. Complete some onboarding flows to see statistics here.</>
                )}
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Scope Indicator */}
              {statistics.scope && (
                <Box sx={{ mb: 2 }}>
                  {statistics.scope.type === 'platform' && (
                    <Chip label="Platform-wide View" icon={<AdminIcon />} color="primary" />
                  )}
                  {statistics.scope.type === 'service_group' && (
                    <Chip label="Service Group View" icon={<GroupIcon />} color="secondary" />
                  )}
                  {statistics.scope.type === 'personal' && (
                    <Chip label="My Clients Only" icon={<PersonIcon />} color="info" />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {statistics.period.label}
                  </Typography>
                </Box>
              )}

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Total Triages
                      </Typography>
                      <Typography variant="h4">{statistics.summary.totalTriages}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        in {statistics.period.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Auto-Approval Rate
                      </Typography>
                      <Typography variant="h4">
                        {statistics.summary.totalTriages > 0
                          ? ((statistics.summary.autoApproved / statistics.summary.totalTriages) * 100).toFixed(1)
                          : 0}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {statistics.summary.autoApproved} auto-approved
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Avg Approval Time
                      </Typography>
                      <Typography variant="h4">{statistics.summary.averageApprovalTime}h</Typography>
                      <Typography variant="body2" color="textSecondary">
                        average response time
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Currently Pending
                      </Typography>
                      <Typography variant="h4">{statistics.summary.currentPending}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        awaiting review
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Manual Reviews
                      </Typography>
                      <Typography variant="h4">{statistics.summary.manuallyApproved}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        manually approved
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Overrides
                      </Typography>
                      <Typography variant="h4">{statistics.summary.overridden}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        AI recommendations overridden
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Charts Grid - Using Box with flexbox instead of Grid v2 for compatibility */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Approval Trend Line Chart */}
                {statistics.approvalTrend && statistics.approvalTrend.length > 0 && (
                  <Box sx={{ flex: '1 1 60%', minWidth: 300 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Approval Trend Over Time
                      </Typography>
                      <LineChart width={500} height={280} data={statistics.approvalTrend} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="auto" stroke="#4caf50" name="Auto-Approved" />
                        <Line type="monotone" dataKey="manual" stroke="#2196f3" name="Manually Approved" />
                        <Line type="monotone" dataKey="overridden" stroke="#ff9800" name="Overridden" />
                      </LineChart>
                    </Paper>
                  </Box>
                )}

                {/* Severity Distribution - CSS-based visualization */}
                {statistics.severityDistribution && (
                  <Box sx={{ flex: '1 1 30%', minWidth: 280 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Severity Distribution
                      </Typography>
                      {(() => {
                        const severityData = [
                          { name: 'Critical', value: statistics.severityDistribution.critical || 0, color: '#f44336' },
                          { name: 'High', value: statistics.severityDistribution.high || 0, color: '#ff9800' },
                          { name: 'Medium', value: statistics.severityDistribution.medium || 0, color: '#2196f3' },
                          { name: 'Low', value: statistics.severityDistribution.low || 0, color: '#4caf50' },
                          { name: 'Minimal', value: statistics.severityDistribution.minimal || 0, color: '#9e9e9e' }
                        ].filter(item => item.value > 0);
                        const total = severityData.reduce((sum, item) => sum + item.value, 0);

                        return (
                          <Box sx={{ mt: 2 }}>
                            {/* Stacked horizontal bar */}
                            <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                              {severityData.map((item) => (
                                <Box
                                  key={item.name}
                                  sx={{
                                    width: `${(item.value / total) * 100}%`,
                                    bgcolor: item.color,
                                    transition: 'width 0.3s'
                                  }}
                                  title={`${item.name}: ${item.value} (${((item.value / total) * 100).toFixed(1)}%)`}
                                />
                              ))}
                            </Box>
                            {/* Legend */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {severityData.map((item) => (
                                <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                  <Box sx={{ width: 12, height: 12, bgcolor: item.color, borderRadius: '50%', mr: 0.5 }} />
                                  <Typography variant="caption">{item.name}: {item.value}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      })()}
                    </Paper>
                  </Box>
                )}

                {/* Diagnosis Distribution - Using simple table instead of BarChart to avoid minPointSize error */}
                {statistics.diagnosisDistribution && (
                  <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Diagnosis Distribution
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {Object.entries(statistics.diagnosisDistribution).map(([diagnosis, count]) => (
                          <Box key={diagnosis} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>{diagnosis}</Typography>
                            <Box sx={{ flex: 2, mx: 2 }}>
                              <Box
                                sx={{
                                  height: 20,
                                  bgcolor: '#2196f3',
                                  borderRadius: 1,
                                  width: `${Math.min(100, (count / Math.max(...Object.values(statistics.diagnosisDistribution))) * 100)}%`
                                }}
                              />
                            </Box>
                            <Typography sx={{ minWidth: 30, textAlign: 'right', fontSize: '0.875rem' }}>{count}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Flows Usage - Using simple table instead of BarChart to avoid minPointSize error */}
                {statistics.flows && statistics.flows.length > 0 && (
                  <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Onboarding Flows Usage
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {statistics.flows.map((flow) => (
                          <Box key={flow.flowName} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>{flow.flowName}</Typography>
                            <Box sx={{ flex: 2, mx: 2 }}>
                              <Box
                                sx={{
                                  height: 20,
                                  bgcolor: '#4caf50',
                                  borderRadius: 1,
                                  width: `${Math.min(100, (flow.totalTriages / Math.max(...statistics.flows.map(f => f.totalTriages))) * 100)}%`
                                }}
                              />
                            </Box>
                            <Typography sx={{ minWidth: 30, textAlign: 'right', fontSize: '0.875rem' }}>{flow.totalTriages}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>

              {/* Top Approvers Table */}
              {statistics.topApprovers && statistics.topApprovers.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Top Approvers
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Approvals</TableCell>
                            <TableCell align="right">Overrides</TableCell>
                            <TableCell align="right">Avg Time (hours)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {statistics.topApprovers.map((approver) => (
                            <TableRow key={approver.userId} sx={{ backgroundColor: approver.isCurrentUser ? '#e3f2fd' : 'inherit' }}>
                              <TableCell>
                                {approver.name}
                                {approver.isCurrentUser && <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />}
                              </TableCell>
                              <TableCell align="right">{approver.approvalCount}</TableCell>
                              <TableCell align="right">{approver.overrideCount}</TableCell>
                              <TableCell align="right">{approver.averageTimeHours}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Triage Assessment Details
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Box>
              {/* Client Information */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Client Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Name:</strong> {selectedReview.client.name || 'Anonymous'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Email:</strong> {selectedReview.client.email}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Age:</strong> {selectedReview.client.age || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Flow:</strong> {selectedReview.flow.name}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Assessment Scores */}
              {selectedReview.assessment && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Assessment Scores</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Total Score:</strong> {selectedReview.assessment?.totalScore || 'N/A'}
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom>Domain Scores:</Typography>
                    <Grid container spacing={1}>
                      {selectedReview.assessment?.domainScores && Object.entries(selectedReview.assessment.domainScores).map(([domain, score]) => (
                        <Grid item xs={6} sm={4} key={domain}>
                          <Chip
                            label={`${domain}: ${score}`}
                            color={getSeverityColor(score, domain)}
                            sx={{ width: '100%' }}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    {selectedReview.assessment?.flaggedDomains?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Flagged Concerns:</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {selectedReview.assessment?.flaggedDomains?.map((flag) => (
                            <Chip key={flag} label={flag} color="warning" size="small" />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Completed Assessments */}
              {selectedReview.assessment && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                      <AssessmentIcon color="primary" />
                      Completed Assessments
                    </Typography>

                    <Accordion defaultExpanded={false}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          '& .MuiAccordionSummary-content': {
                            overflow: 'visible'
                          }
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                          <Box flex={1} mr={2}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              {selectedReview.assessment.testFullName || selectedReview.assessment.testShortName || 'Assessment'}
                            </Typography>
                            {selectedReview.assessment.testDescription && (
                              <Typography variant="caption" color="text.secondary">
                                {selectedReview.assessment.testDescription}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" gap={1} alignItems="center" flexShrink={0}>
                            <Chip
                              label={`Score: ${selectedReview.assessment.totalScore}${selectedReview.assessment.maxPossibleScore
                                ? `/${selectedReview.assessment.maxPossibleScore}`
                                : ''
                                }`}
                              color={getScoreSeverityColor(
                                selectedReview.assessment.totalScore,
                                selectedReview.assessment.maxPossibleScore
                              )}
                              size="medium"
                            />
                            {selectedReview.assessment.completedAt && (
                              <Chip
                                label={new Date(selectedReview.assessment.completedAt).toLocaleDateString()}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails>
                        {/* Interpretation */}
                        {selectedReview.assessment.interpretation && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              <strong>Interpretation:</strong> {selectedReview.assessment.interpretation}
                            </Typography>
                          </Alert>
                        )}

                        {/* Subscale Scores (if available) */}
                        {selectedReview.assessment.subscaleScores?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Subscale Scores:
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {selectedReview.assessment.subscaleScores.map((subscale, idx) => (
                                <Chip
                                  key={idx}
                                  label={`${subscale.name}: ${subscale.score}/${subscale.maxPossible}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Question & Answer Details */}
                        {selectedReview.assessment.responses?.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                              Assessment Responses ({selectedReview.assessment.responses.length} questions):
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {selectedReview.assessment.responses.map((response, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  mb: 2,
                                  pb: 2,
                                  borderBottom:
                                    idx < selectedReview.assessment.responses.length - 1
                                      ? '1px solid #e0e0e0'
                                      : 'none'
                                }}
                              >
                                <Typography variant="body2" fontWeight="bold" color="text.primary">
                                  Q{idx + 1}: {response.questionText}
                                </Typography>
                                <Typography variant="body2" color="primary" sx={{ mt: 0.5, pl: 2 }}>
                                  Answer: <strong>{response.answerLabel || response.answerValue}</strong>
                                </Typography>
                              </Box>
                            ))}
                          </>
                        )}

                        {(!selectedReview.assessment.responses || selectedReview.assessment.responses.length === 0) && (
                          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                            No response details available
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              )}

              {/* AI Recommendation */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <AutoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    AI Recommendation
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Recommended Persona:</Typography>
                    <Chip
                      icon={<PersonIcon />}
                      label={selectedReview.recommendation.recommendedPersonaKey}
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Reason:</Typography>
                    <Typography variant="body2">{selectedReview.recommendation.reason}</Typography>
                  </Box>

                  {selectedReview.recommendation.matchedRule && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Matched Rule:</Typography>
                      <Typography variant="body2">
                        <strong>{selectedReview.recommendation.matchedRule.name}</strong>
                        {' '}(Priority: {selectedReview.recommendation.matchedRule.priority})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedReview.recommendation.matchedRule.description}
                      </Typography>
                    </Box>
                  )}

                  {selectedReview.recommendation.alternativePersonas?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2">Alternative Personas:</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {selectedReview.recommendation.alternativePersonas.map((alt) => (
                          <Chip
                            key={alt.key}
                            label={alt.name || alt.key}
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Strategy Information */}
              {selectedReview.strategy && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Strategy Used</Typography>
                    <Typography variant="body2"><strong>Name:</strong> {selectedReview.strategy.name}</Typography>
                    <Typography variant="body2"><strong>Key:</strong> {selectedReview.strategy.key}</Typography>
                    <Typography variant="body2"><strong>Mode:</strong> {selectedReview.strategy.defaultMode}</Typography>
                    {selectedReview.strategy.description && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {selectedReview.strategy.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Approval History (for approved triages) */}
              {selectedReview.approval && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Approval History
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Approval Type:</strong>
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {(() => {
                            const approvalInfo = formatApprovalType(selectedReview.approval.type);
                            return (
                              <Chip
                                icon={approvalInfo.icon}
                                label={approvalInfo.label}
                                color={approvalInfo.color}
                                size="small"
                              />
                            );
                          })()}
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Approved By:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {selectedReview.approval.approvedBy ? (
                            <>
                              {selectedReview.approval.approvedBy.name || selectedReview.approval.approvedBy.email}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {selectedReview.approval.approvedBy.role?.join(', ')}
                              </Typography>
                            </>
                          ) : (
                            <Chip label="System (Automatic)" size="small" color="primary" />
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Approval Date:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {selectedReview.approval.approvedAt ? (
                            <>
                              {new Date(selectedReview.approval.approvedAt).toLocaleDateString()}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(selectedReview.approval.approvedAt).toLocaleTimeString()}
                              </Typography>
                            </>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Assigned Persona:</strong>
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            icon={<PersonIcon />}
                            label={selectedReview.assignedPersona || selectedReview.recommendation?.recommendedPersonaKey || 'N/A'}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Grid>

                      {/* Show override information if applicable */}
                      {selectedReview.approval.type === 'overridden' && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Override Information
                            </Typography>
                            <Typography variant="body2">
                              <strong>Original Recommendation:</strong> {selectedReview.recommendation?.recommendedPersonaKey || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Override Reason:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                              {selectedReview.approval.overrideReason || 'No reason provided'}
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Assessment Responses */}
              {selectedReview.assessment && selectedReview.assessment.responses && selectedReview.assessment.responses.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">View Full Assessment Responses ({selectedReview.assessment.responses.length} questions)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {selectedReview.assessment.responses.map((response, idx) => (
                      <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: idx < selectedReview.assessment.responses.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Typography variant="body2" fontWeight="bold">
                          Q{idx + 1}: {response.questionText}
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                          Answer: {response.answerLabel || response.answerValue}
                        </Typography>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Override Dialog */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Override Persona Assignment</DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Client:</strong> {selectedReview.client.name || 'Anonymous'}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                <strong>AI Recommendation:</strong> {selectedReview.recommendation.recommendedPersonaKey}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <TextField
                select
                fullWidth
                label="Select Persona"
                value={overridePersonaKey}
                onChange={(e) => setOverridePersonaKey(e.target.value)}
                sx={{ mb: 2 }}
                required
              >
                {availablePersonas.map((persona) => (
                  <MenuItem key={persona.key} value={persona.key}>
                    {persona.name} ({persona.key})
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Override Reason (required, min 10 characters)"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Explain why you are overriding the AI recommendation..."
                required
                helperText={`${overrideReason.length} / 10 characters minimum`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleOverrideSubmit}
            variant="contained"
            color="warning"
            disabled={!overridePersonaKey || overrideReason.trim().length < 10}
          >
            Override and Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars for feedback */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TriageReviewView;
